/**
 * RequireAdmin — guard para rutas /app/admin/*. Solo deja pasar si
 * el SessionUser activo tiene rol "superuser". El backend además
 * gateaba con RBAC inline (FR-009), pero este guard cliente evita
 * navegación a páginas que el server rechazaría con 403.
 *
 * Asume que <RequireAuth> ya validó que hay sesión activa. Si por
 * alguna razón GetSession retorna null acá, redirige a /login.
 */
import { type ReactElement, useEffect, useState } from "react";
import { Navigate } from "react-router";

import { RouteSkeleton } from "../../components/RouteSkeleton";
import { getSession, type SessionUser } from "../../features/login/lib/auth";

interface RequireAdminProps {
  children: ReactElement;
}

type State =
  | { status: "checking" }
  | { status: "ok"; user: SessionUser }
  | { status: "forbidden" }
  | { status: "ko" };

export function RequireAdmin({ children }: RequireAdminProps): ReactElement {
  const [state, setState] = useState<State>({ status: "checking" });

  useEffect(() => {
    let cancelled = false;
    getSession()
      .then((user) => {
        if (cancelled) return;
        if (!user) {
          setState({ status: "ko" });
          return;
        }
        if (user.rol !== "superuser") {
          setState({ status: "forbidden" });
          return;
        }
        setState({ status: "ok", user });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "ko" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === "checking") {
    return <RouteSkeleton variant="app" />;
  }
  if (state.status === "ko") {
    return <Navigate to="/login" replace />;
  }
  if (state.status === "forbidden") {
    return <Navigate to="/app" replace />;
  }
  return children;
}
